import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { RootState } from "../../../redux/store";

import {
  CableProviderState,
  CableProviderResponse,
  CableProvider,
} from "./types/cable-types";
import { ApiError } from "./types/common-types";
import { PaginationReturn } from "./types/types";

const initialState: CableProviderState = {
  cableProviders: [],
  cableProvider: null,
  selectedCableProvider: null,
  loading: true,
  error: null,
  success: false,
  total: 0,
  perPage: 15,
  currentPage: 1,
  lastPage: 1,
  pagination: {
    nextPageUrl: null,
    prevPageUrl: null,
  },
};

// API Base URL
const API_URL = process.env.REACT_APP_API_URL;

// CREATE CableProvider
export const createCableProvider = createAsyncThunk<
  CableProviderResponse,
  FormData | Omit<CableProvider, "id" | "created_at" | "updated_at">,
  { rejectValue: ApiError; state: RootState }
>("dataProviders/create", async (cableProviderData, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const isFormData = cableProviderData instanceof FormData;
    const res = await axios.post(
      `${API_URL}/cable-providers`,
      cableProviderData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
        },
      }
    );
    return {
      message: res.data.message,
      dataProvider: res.data.data,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to create data provider" }
    );
  }
});

// READ (fetch all) CableProviders
export const fetchCableProviders = createAsyncThunk<
  CableProviderResponse & PaginationReturn,
  void | { url: string },
  { rejectValue: ApiError; state: RootState }
>("dataProviders/fetchAll", async (urlFilters, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const url: string | undefined = urlFilters?.url;
    const apiUrl = url || `${API_URL}/cable-providers`;
    const res = await axios.get(apiUrl, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return {
      message: res.data.data.message,
      cableProviders: res.data.data.data,
      total: res.data.data.total,
      per_page: res.data.data.per_page,
      current_page: res.data.data.current_page,
      last_page: res.data.data.last_page,
      prev_page_url: res.data.data.prev_page_url,
      next_page_url: res.data.data.next_page_url,
    };
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch cable providers" }
    );
  }
});

// READ (fetch one) CableProvider by ID
export const fetchCableProviderById = createAsyncThunk<
  CableProvider,
  number,
  { rejectValue: ApiError; state: RootState }
>("cableProviders/fetchById", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const res = await axios.get(`${API_URL}/cable-providers/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to fetch cable provider" }
    );
  }
});

// UPDATE DataProvider
export const updateCableProvider = createAsyncThunk<
  CableProvider,
  { id: number; data: Partial<CableProvider> },
  { rejectValue: ApiError; state: RootState }
>("cableProviders/update", async ({ id, data }, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    const isFormData = data instanceof FormData;
    const res = await axios.post(
      `${API_URL}/cable-providers/${id}?_method=PUT`,
      data,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(isFormData ? { "Content-Type": "multipart/form-data" } : {}),
        },
      }
    );
    return res.data.data;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to update cable provider" }
    );
  }
});

// DELETE CableProvider
export const deleteCableProvider = createAsyncThunk<
  number,
  number,
  { rejectValue: ApiError; state: RootState }
>("cableProviders/delete", async (id, thunkAPI) => {
  const token = thunkAPI.getState().auth.token;
  if (!token) return thunkAPI.rejectWithValue({ message: "Unauthorized" });

  try {
    await axios.delete(`${API_URL}/cable-providers/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return id;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(
      err.response?.data || { message: "Failed to delete cable provider" }
    );
  }
});

// Slice
const cableProviderSlice = createSlice({
  name: "cableProviders",
  initialState,
  reducers: {
    resetCableProviderState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.selectedCableProvider = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Create
      .addCase(createCableProvider.pending, (state) => {
        state.loading = true;
        state.success = false;
        state.error = null;
      })
      .addCase(createCableProvider.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        if (action.payload.cableProvider) {
          state.cableProviders.unshift(action.payload.cableProvider);
        }
      })
      .addCase(createCableProvider.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to create cable provider";
      })

      // Fetch All
      .addCase(fetchCableProviders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCableProviders.fulfilled, (state, action) => {
        state.loading = false;
        state.cableProviders = action.payload.cableProviders || [];
        state.total = action.payload.total || 0;
        state.perPage = action.payload.per_page || 15;
        state.currentPage = action.payload.current_page || 1;
        state.lastPage = action.payload.last_page || 1;
        state.pagination.nextPageUrl = action.payload.next_page_url;
        state.pagination.prevPageUrl = action.payload.prev_page_url;
      })
      .addCase(fetchCableProviders.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch cable providers";
      })

      // Fetch One
      .addCase(fetchCableProviderById.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.selectedCableProvider = null;
      })
      .addCase(fetchCableProviderById.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedCableProvider = action.payload;
      })
      .addCase(fetchCableProviderById.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to fetch cable provider";
      })

      // Update
      .addCase(updateCableProvider.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(updateCableProvider.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.cableProviders = state.cableProviders.map((provider) =>
          provider.id === action.payload.id ? action.payload : provider
        );
        if (state.selectedCableProvider?.id === action.payload.id) {
          state.selectedCableProvider = action.payload;
        }
      })
      .addCase(updateCableProvider.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to update cable provider";
      })

      // Delete
      .addCase(deleteCableProvider.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(deleteCableProvider.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.cableProviders = state.cableProviders.filter(
          (provider) => provider.id !== action.payload
        );
      })
      .addCase(deleteCableProvider.rejected, (state, action) => {
        state.loading = false;
        state.error =
          action.payload?.message || "Failed to delete cable provider";
      });
  },
});

export const { resetCableProviderState } = cableProviderSlice.actions;
export default cableProviderSlice.reducer;
